import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Accordion, Card, Button } from 'react-bootstrap';
import { isArray, cloneDeep } from 'lodash';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { customStylesProductAddEdit } from '../helper';
import { toasterMsg } from '../Toaster/Toaster';
import Loader from '../Utility/Loader/Loader';

function ProductIdentifier({ editedProductData, showLabel, sizeOptions, lengthOptions, colorOptions, refs }) {
  const reducer = useSelector((reducerstate) => reducerstate);
  const { SystemMastersDetails } = reducer;
  const { productIdentifierList, identifierType } = SystemMastersDetails;

  const [state, setState] = useState({
    storedData: [],
    loader: false,
    sizeOptionModified: [],
    isTouched: false,
    isSelectChanged: false,
    keysToCheck: [],
    sizeKeyId: '',
    lengthKeyId: '',
    productNumberKeyId: '',
    isOpen: false,
    activeProductIdentifierList: [],
  });

  const {
    storedData,
    loader,
    sizeOptionModified,
    isTouched,
    isSelectChanged,
    keysToCheck,
    sizeKeyId,
    lengthKeyId,
    productNumberKeyId,
    isOpen,
    activeProductIdentifierList,
  } = state;
  const { t } = useTranslation('common');

  const combinationData = async () => {
    const sizeArr = editedProductData?.sizeRanges || [];
    const lengthArr = editedProductData?.legLengths || [];
    const colourArr = editedProductData?.colourLibraries || [];
    const combinedArr = [];

    if (isArray(colourArr) && colourArr?.length && isArray(lengthArr) && lengthArr?.length && isArray(sizeArr) && sizeArr?.length) {
      colourArr.forEach((colour) => {
        sizeArr.forEach((size) => {
          lengthArr.forEach((length) => {
            const obj = {};
            activeProductIdentifierList.forEach((item) => {
              switch (item.name) {
                // eslint-disable-next-line sonarjs/no-duplicate-string
                case 'Product number':
                  obj[item?._id] = editedProductData?.number;
                  break;
                case 'Colourway':
                  obj[item?._id] = colour;
                  break;
                case 'Size':
                  obj[item?._id] = size;
                  break;
                case 'Length':
                  obj[item?._id] = length;
                  break;

                default:
                  obj[item?._id] = '';
              }
            });

            combinedArr.push(obj);
          });
        });
      });
    } else if (isArray(colourArr) && colourArr?.length && isArray(lengthArr) && lengthArr?.length) {
      colourArr.forEach((colour) => {
        lengthArr.forEach((length) => {
          const obj = {};
          activeProductIdentifierList.forEach((item) => {
            switch (item.name) {
              case 'Product number':
                obj[item?._id] = editedProductData?.number;
                break;
              case 'Colourway':
                obj[item?._id] = colour;
                break;
              case 'Length':
                obj[item?._id] = length;
                break;

              default:
                obj[item?._id] = '';
            }
          });
          combinedArr.push(obj);
        });
      });
    } else if (isArray(colourArr) && colourArr?.length && isArray(sizeArr) && sizeArr?.length) {
      colourArr.forEach((colour) => {
        sizeArr.forEach((size) => {
          const obj = {};
          activeProductIdentifierList.forEach((item) => {
            switch (item.name) {
              case 'Product number':
                obj[item?._id] = editedProductData?.number;
                break;
              case 'Colourway':
                obj[item?._id] = colour;
                break;
              case 'Size':
                obj[item?._id] = size;
                break;
              default:
                obj[item?._id] = '';
            }
          });
          combinedArr.push(obj);
        });
      });
    } else if (isArray(lengthArr) && lengthArr?.length && isArray(sizeArr) && sizeArr?.length) {
      sizeArr.forEach((size) => {
        lengthArr.forEach((length) => {
          const obj = {};
          activeProductIdentifierList.forEach((item) => {
            switch (item.name) {
              case 'Product number':
                obj[item?._id] = editedProductData?.number;
                break;
              case 'Size':
                obj[item?._id] = size;
                break;
              case 'Length':
                obj[item?._id] = length;
                break;

              default:
                obj[item?._id] = '';
            }
          });
          combinedArr.push(obj);
        });
      });
    } else if (isArray(lengthArr) && lengthArr?.length) {
      lengthArr.forEach((length) => {
        const obj = {};
        activeProductIdentifierList.forEach((item) => {
          switch (item.name) {
            case 'Product number':
              obj[item?._id] = editedProductData?.number;
              break;
            case 'Length':
              obj[item?._id] = length;
              break;

            default:
              obj[item?._id] = '';
          }
        });
        combinedArr.push(obj);
      });
    } else if (isArray(sizeArr) && sizeArr?.length) {
      sizeArr.forEach((size) => {
        const obj = {};
        activeProductIdentifierList.forEach((item) => {
          switch (item.name) {
            case 'Product number':
              obj[item?._id] = editedProductData?.number;
              break;
            case 'Size':
              obj[item?._id] = size;
              break;
            default:
              obj[item?._id] = '';
          }
        });
        combinedArr.push(obj);
      });
    } else if (isArray(colourArr) && colourArr?.length) {
      colourArr.forEach((colour) => {
        const obj = {};
        activeProductIdentifierList.forEach((item) => {
          switch (item.name) {
            case 'Product number':
              obj[item?._id] = editedProductData?.number;
              break;
            case 'Colourway':
              obj[item?._id] = colour;
              break;
            default:
              obj[item?._id] = '';
          }
        });
        combinedArr.push(obj);
      });
    }

    return combinedArr;
  };

  // Fetch the data when the product identifier is edited
  useEffect(() => {
    const getData = async () => {
      setState((prevData) => ({
        ...prevData,
        loader: true,
      }));

      let savedData = [];

      if (identifierType) {
        const newCombinationData = await combinationData();

        if (storedData?.length > 0) {
          const modifiedArray = newCombinationData?.map((obj) => {
            const match = storedData.find((dataObj) => keysToCheck?.every((key) => obj[key] === dataObj[key]));
            return match ? { ...match } : { ...obj };
          });
          savedData = modifiedArray;
        } else {
          savedData = newCombinationData;
        }
      } else if (storedData?.length === 0) {
        const objManualData = {};
        activeProductIdentifierList.forEach((item) => {
          if (item.name === 'Product number') {
            objManualData[item?._id] = editedProductData?.number;
          } else {
            objManualData[item?._id] = '';
          }
        });
        savedData.push(objManualData);
      } else {
        savedData = storedData;
      }
      savedData = await savedData.map((obj) => {
        const newObj = { ...obj };
        newObj[productNumberKeyId] = editedProductData?.number;
        return newObj;
      });
      setState((prevData) => ({
        ...prevData,
        storedData: savedData,
        loader: false,
      }));
    };

    if (productIdentifierList?.length > 0 && isTouched) getData();
  }, [
    editedProductData?.number?.length,
    editedProductData?.sizeRanges?.length,
    editedProductData?.legLengths?.length,
    editedProductData?.colourLibraries?.length,
    productIdentifierList?.length,
    identifierType,
    isTouched,
  ]);

  // Set the static keys to check
  useEffect(() => {
    if (productIdentifierList?.length > 0) {
      const staticKeys = [];
      productIdentifierList?.forEach((item) => {
        if (item.name === 'Colourway' || item.name === 'Size' || item.name === 'Length') {
          staticKeys.push(item._id);
        }
      });

      setState((prevData) => ({
        ...prevData,
        keysToCheck: staticKeys,
        sizeKeyId: productIdentifierList.find((item) => item.name === 'Size')?._id,
        lengthKeyId: productIdentifierList.find((item) => item.name === 'Length')?._id,
        productNumberKeyId: productIdentifierList.find((item) => item.name === 'Product number')?._id,
        activeProductIdentifierList: productIdentifierList.filter((item) => item.isActive),
      }));
    }
  }, [productIdentifierList]);

  // Set the stored data when the product identifier is edited
  useEffect(() => {
    if (editedProductData?.productIndentifier?.length > 0) {
      setState((prevData) => ({
        ...prevData,
        storedData: editedProductData.productIndentifier,
        isTouched: true,
      }));
    } else if (productIdentifierList?.length > 0) {
      setState((prevData) => ({
        ...prevData,
        isTouched: true,
      }));
    }
  }, [editedProductData?.productIndentifier?.length, productIdentifierList]);

  // Modify the size options and Empty size and length when size options are changed
  useEffect(() => {
    if (sizeOptions?.length > 0) {
      const uniqueArray = sizeOptions.reduce((acc, current) => {
        const isDuplicate = acc.some((item) => item.label === current.label && item.value === current.value);
        if (!isDuplicate) {
          acc.push(current);
        }
        return acc;
      }, []);
      const modifiedArray = uniqueArray.map((item) => {
        return {
          name: item.label,
          _id: item.value,
        };
      });
      setState((prevData) => ({
        ...prevData,
        sizeOptionModified: modifiedArray,
      }));
    } else if (isTouched) {
      const copyData = cloneDeep(storedData);
      copyData.forEach((obj) => {
        const newObj = { ...obj };
        newObj[sizeKeyId] = '';
        // Replace the original object with the modified one
        Object.assign(obj, newObj);
      });
      setState((prevData) => ({
        ...prevData,
        storedData: copyData,
        sizeOptionModified: [],
        isSelectChanged: true,
      }));
    }
  }, [sizeOptions?.length, isTouched]);

  // Check Unique combination
  useEffect(() => {
    if (isSelectChanged && storedData?.length > 0) {
      const seenCombinations = new Set();
      const copyData = cloneDeep(storedData);
      copyData.forEach((obj) => {
        const combination = keysToCheck.map((key) => obj[key]).join('-');

        if (seenCombinations.has(combination)) {
          toasterMsg(t('DEVELOPMENT_ORDER.COMBINATION_EXIST'), 'danger');
          // Make a shallow copy of the object
          const newObj = { ...obj };
          keysToCheck.forEach((key) => {
            newObj[key] = '';
          });
          // Replace the original object with the modified one
          Object.assign(obj, newObj);
        } else if (obj[sizeKeyId] === '') {
          const newObj = { ...obj };
          newObj[lengthKeyId] = '';
          Object.assign(obj, newObj);
        } else {
          seenCombinations.add(combination);
        }
      });

      setState((prevData) => ({
        ...prevData,
        storedData: copyData,
        isSelectChanged: false,
      }));
    }
  }, [storedData, isSelectChanged]);

  const handleOpenProductIdentifier = async () => {
    setState((prevData) => ({
      ...prevData,
      isOpen: !isOpen,
    }));
  };

  const getValue = (keyId, data) => {
    const getNameById = productIdentifierList.find((ele) => ele._id === keyId)?.name;
    if (getNameById === 'Colourway' || getNameById === 'Length') {
      return showLabel(data[keyId], getNameById);
    }
    return data[keyId];
  };

  const handleInput = (value, rowIndex, key) => {
    const copyData = cloneDeep(storedData);
    copyData[rowIndex][key] = value;
    setState((prevData) => ({
      ...prevData,
      storedData: copyData,
    }));
  };

  const handleSelect = (value, rowIndex, key) => {
    const copyData = cloneDeep(storedData);
    copyData[rowIndex][key] = value?._id || '';
    setState((prevData) => ({
      ...prevData,
      storedData: copyData,
      isSelectChanged: true,
    }));
  };

  useImperativeHandle(refs, () => ({
    getProductIdentifierData() {
      return storedData;
    },
  }));

  const getOptions = (keyName) => {
    switch (keyName) {
      case 'Colourway':
        return colorOptions;
      case 'Size':
        return sizeOptionModified;
      case 'Length':
        return lengthOptions;

      default:
        return [];
    }
  };

  const handleAddRow = () => {
    const copyData = cloneDeep(storedData);
    const objManualData = {};

    activeProductIdentifierList.forEach((item) => {
      if (item.name === 'Product number') {
        objManualData[item?._id] = editedProductData?.number;
      } else {
        objManualData[item?._id] = '';
      }
    });
    copyData.push(objManualData);
    setState((prevData) => ({
      ...prevData,
      storedData: copyData,
    }));
  };

  const getSelectValue = (keyName, value) => {
    const selectObj = getOptions(keyName).find((ele) => ele._id === value);

    return selectObj || null;
  };

  const handleDeleteRow = (rowIndex) => {
    const copyData = cloneDeep(storedData);
    copyData.splice(rowIndex, 1);
    setState((prevData) => ({
      ...prevData,
      storedData: copyData,
    }));
  };

  const checkIsActive = (eleIdentifier) => {
    const { _id, isActive } = eleIdentifier;
    return keysToCheck.includes(_id) ? true : isActive;
  };

  const checkDisabledCriteria = (keyName, rowIndex) => {
    return keyName === 'Length' && storedData[rowIndex][sizeKeyId] === '';
  };

  return (
    <div className="collapse-card mt-3 d-none">
      <Accordion>
        <Card className="mb-3">
          <Card.Header>
            {t('DEVELOPMENT_ORDER.PRODUCT_IDENTIFIER_TITLE')}
            <Accordion.Toggle className="btn-toggle" onClick={() => handleOpenProductIdentifier()} as={Button} variant="link" eventKey="0">
              {isOpen ? <i className="icon-new-down-arrow-icon icon-inverse" /> : <i className="icon-new-down-arrow-icon " />}
            </Accordion.Toggle>
          </Card.Header>
          <Accordion.Collapse eventKey="0">
            {loader ? (
              <Loader />
            ) : (
              <div className="product-identifier-scrollable">
                <table className="table custom-table layout-fixed m-0">
                  <thead>
                    <tr>
                      {productIdentifierList?.map((eleItem) => {
                        return identifierType
                          ? checkIsActive(eleItem) && (
                              <th className="w-150px" key={eleItem?._id}>
                                {eleItem.name}
                              </th>
                            )
                          : eleItem?.isActive && (
                              <th className="w-150px" key={eleItem?._id}>
                                {eleItem.name}
                              </th>
                            );
                      })}
                      {!identifierType && <th>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {storedData?.length > 0 ? (
                      storedData?.map((dataRow, rowIndex) => (
                        <tr key={rowIndex}>
                          {productIdentifierList.map((eleIdentifier, colIndex) =>
                            identifierType
                              ? checkIsActive(eleIdentifier) && (
                                  <td key={`${rowIndex}-${colIndex}`}>
                                    {eleIdentifier?.recordType === 'textBox' ? (
                                      <input
                                        className="form-control text-bordered"
                                        onChange={(e) => handleInput(e.target.value, rowIndex, eleIdentifier?._id)}
                                        type="text"
                                        value={dataRow[eleIdentifier?._id]}
                                      />
                                    ) : (
                                      getValue(eleIdentifier?._id, dataRow)
                                    )}
                                  </td>
                                )
                              : eleIdentifier?.isActive && (
                                  <td key={`${rowIndex}-${colIndex}`}>
                                    {eleIdentifier?.recordType === 'textBox' && (
                                      <input
                                        className="form-control text-bordered"
                                        onChange={(e) => handleInput(e.target.value, rowIndex, eleIdentifier?._id)}
                                        type="text"
                                        value={dataRow[eleIdentifier?._id]}
                                      />
                                    )}
                                    {eleIdentifier?.recordType === 'dropdown' && (
                                      <Select
                                        options={getOptions(eleIdentifier?.name)}
                                        value={getSelectValue(eleIdentifier?.name, dataRow[eleIdentifier?._id])}
                                        onChange={(e) => handleSelect(e, rowIndex, eleIdentifier?._id)}
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option._id}
                                        styles={customStylesProductAddEdit}
                                        menuPosition="fixed"
                                        menuPlacement="top"
                                        maxMenuHeight={130}
                                        isDisabled={checkDisabledCriteria(eleIdentifier?.name, rowIndex)}
                                        isClearable
                                      />
                                    )}
                                    {eleIdentifier?.recordType !== 'textBox' &&
                                      eleIdentifier?.recordType !== 'dropdown' &&
                                      getValue(eleIdentifier?._id, dataRow)}
                                  </td>
                                ),
                          )}
                          {!identifierType && (
                            <td className="text-center">
                              {rowIndex === 0 ? (
                                <button type="button" className="btn" onClick={() => handleAddRow()}>
                                  <i className="icon-add" />
                                </button>
                              ) : (
                                <button type="button" className="btn" onClick={() => handleDeleteRow(rowIndex)}>
                                  <i className="icon-delete" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="text-center" colSpan={productIdentifierList?.length}>
                          <p className="m-0">{t('DEVELOPMENT_ORDER.NO_RECORD')}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </div>
  );
}
const ForwardedProductIdentifier = forwardRef(ProductIdentifier);
export default ForwardedProductIdentifier;
